import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useClientPoint,
  useFloating,
  UseFloatingReturn,
  useHover,
  useInteractions,
  UseInteractionsReturn,
} from '@floating-ui/react';
import { debounce } from 'lodash-es';
import { AnimatePresence } from 'motion/react';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { TOTAL_HOURS } from '../../constants';
import { useCalendarGridStore } from '../../store/calendar-grid.store';
import { getHoverMinutesFromEvent, minutesToTimeStr } from '../../utils';
import { TooltipContainer } from './tooltip-container';

type UseTooltipReturn = {
  hoverMinutes?: number;
  hoveredEventId?: string;
  onMouseMove: (x: number, y: number, hoveredEventId?: string) => void;
  hideTooltip: () => void;
  getReferenceProps: UseInteractionsReturn['getReferenceProps'];
  getFloatingProps: UseInteractionsReturn['getFloatingProps'];
  refs: UseFloatingReturn['refs'];
  floatingStyles: UseFloatingReturn['floatingStyles'];
};

type HoverState = {
  hoverMinutes?: number;
  hoveredEventId?: string;
};

const STEADY_DELAY_MS = 700;
const MOVEMENT_EPSILON_PX = 1;
const HIDE_THRESHOLD_PX = 5;

export const useCalendarTooltip = (containerRef: React.RefObject<HTMLElement>): UseTooltipReturn => {
  const { pxPerMinute } = useCalendarGridStore(['pxPerMinute']);
  const [hoverState, setHoverState] = useState<HoverState>({});
  const { refs, floatingStyles, context } = useFloating({
    placement: 'top',
    elements: { reference: containerRef.current },
    middleware: [offset(20), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });
  const hover = useHover(context);
  const clientPoint = useClientPoint(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, clientPoint]);

  const lastPos = useRef<{ x: number; y: number } | null>(null);
  const showTooltipDebounced = useMemo(
    () =>
      debounce((yPosition: number, hoveredEventId?: string) => {
        const totalMinutes = getHoverMinutesFromEvent(yPosition, containerRef.current as HTMLElement, pxPerMinute);
        setHoverState({ hoverMinutes: totalMinutes, hoveredEventId });
      }, STEADY_DELAY_MS),
    [containerRef, pxPerMinute]
  );
  const hideTooltip = useCallback(() => {
    setHoverState({});
    showTooltipDebounced.cancel();
  }, [showTooltipDebounced]);

  const onMouseMove = useCallback(
    (clientX: number, clientY: number, hoveredEventId?: string) => {
      const prev = lastPos.current;
      lastPos.current = { x: clientX, y: clientY };

      if (!prev) {
        hideTooltip();
        showTooltipDebounced(clientY, hoveredEventId);
        return;
      }

      const deltaX = Math.abs(prev.x - clientX);
      const deltaY = Math.abs(prev.y - clientY);
      const hasMovement = deltaX >= MOVEMENT_EPSILON_PX || deltaY >= MOVEMENT_EPSILON_PX;

      if (!hasMovement) {
        return;
      }

      const hasSignificantMovement = deltaX >= HIDE_THRESHOLD_PX || deltaY >= HIDE_THRESHOLD_PX;
      if (hasSignificantMovement && hoverState.hoverMinutes !== undefined) {
        hideTooltip();
      }

      showTooltipDebounced(clientY, hoveredEventId);
    },
    [hideTooltip, hoverState.hoverMinutes, showTooltipDebounced]
  );
  useEffect(() => () => showTooltipDebounced.cancel(), [showTooltipDebounced]);

  return {
    hoverMinutes: hoverState.hoverMinutes,
    hoveredEventId: hoverState.hoveredEventId,
    onMouseMove,
    hideTooltip,
    getReferenceProps,
    getFloatingProps,
    refs,
    floatingStyles,
  };
};

interface Props {
  hoverMinutes?: number;
  hoveredEventId?: string;
  containerRef: UseFloatingReturn['refs']['setFloating'];
  floatingStyles: UseFloatingReturn['floatingStyles'];
  getFloatingProps: UseInteractionsReturn['getFloatingProps'];
}

const EventTooltipContent = ({ hoveredEventId }: { hoveredEventId: string }) => {
  const hoveredEvent = useCalendarGridStore((state) => state.eventsById[hoveredEventId]);

  if (!hoveredEvent) {
    return null;
  }

  return (
    <div css={eventTooltipContentStyle}>
      <Text weight='bold' size='medium' color='white'>
        {hoveredEvent.eventType}
      </Text>
      <Text size='small' color='white'>
        {hoveredEvent.startTime} • {hoveredEvent.duration ?? 0}m
      </Text>
    </div>
  );
};

export const CalendarTooltip = memo(
  ({ hoverMinutes, hoveredEventId, containerRef, floatingStyles, getFloatingProps }: Props) => {
    const hoverTime = minutesToTimeStr(hoverMinutes ?? 0);
    const shouldShow = hoverMinutes !== undefined && hoverMinutes > 0 && hoverMinutes < TOTAL_HOURS * 60;

    return (
      <>
        <AnimatePresence>
          {shouldShow && (
            <FloatingPortal>
              <div
                ref={containerRef}
                style={floatingStyles}
                {...getFloatingProps()}
                css={{ pointerEvents: 'none' }}
                data-testid='calendar-tooltip'
              >
                <TooltipContainer>
                  {hoveredEventId ? <EventTooltipContent hoveredEventId={hoveredEventId} /> : hoverTime}
                </TooltipContainer>
              </div>
            </FloatingPortal>
          )}
        </AnimatePresence>
      </>
    );
  }
);
CalendarTooltip.displayName = 'CalendarTooltip';

const eventTooltipContentStyle = {
  display: 'grid',
  gap: theme.spacing(0.25),
};
